import {
  UserPlus, CreditCard, BookOpen, ClipboardList, Clock,
  Users, DollarSign, Award, Briefcase, MessageSquare,
  BarChart2, Heart, Calendar, FileText, Library, Bell
} from "lucide-react";

const features = {
  admissions: {
    slug: "admissions",
    icon: UserPlus,
    label: "Admissions",
    headline: "Admissions,\nautomated.",
    tagline: "01 — Student Lifecycle",
    description: "End-to-end admission management — from application forms to merit lists to enrollment. Track every applicant through customizable pipelines with real-time status updates.",
    screenshot: "/screenshots/admissions.png",
    color: "#4B7BF5",
    stats: [
      { value: "15K+", label: "Applications processed" },
      { value: "4x", label: "Faster processing" },
      { value: "Zero", label: "Paper forms" },
    ],
    capabilities: [
      { title: "Smart Application Forms", desc: "Dynamic forms that adapt fields based on program, category, and quota. Auto-validate documents and eligibility in real-time." },
      { title: "Merit List Engine", desc: "Generate merit lists across categories, quotas, and reservation policies. Auto-rank with configurable weightage for marks, entrance scores, and extracurriculars." },
      { title: "Pipeline Tracking", desc: "Kanban-style pipeline from Applied → Shortlisted → Admitted → Enrolled. Bulk actions, filters, and one-click status updates." },
      { title: "Document Verification", desc: "Upload, verify, and archive admission documents. Flag discrepancies automatically and notify applicants via WhatsApp." },
    ],
    keywords: "college admission management, online admission form, student enrollment system, merit list generation, admission tracking software",
  },
  fees: {
    slug: "fees",
    icon: CreditCard,
    label: "Fee Collection",
    headline: "Fees,\ncollected.",
    tagline: "02 — Finance",
    description: "Complete fee lifecycle management with online payments, auto-reminders, receipt generation, and real-time collection dashboards. Support for installments, scholarships, and fine calculations.",
    screenshot: "/screenshots/fees.png",
    color: "#22C55E",
    stats: [
      { value: "₹42L+", label: "Collected monthly" },
      { value: "87%", label: "Collection rate" },
      { value: "< 2min", label: "Receipt generation" },
    ],
    capabilities: [
      { title: "Multi-mode Payments", desc: "Accept fees via UPI, cards, net banking, and cash. QR code generation for instant payments. Auto-reconciliation with bank statements." },
      { title: "Smart Reminders", desc: "Automated fee reminders via WhatsApp and SMS. Escalation workflows for overdue accounts with configurable grace periods." },
      { title: "Scholarship Engine", desc: "Apply merit-based, need-based, and category-based scholarships automatically. Track disbursements and adjustments." },
      { title: "Financial Reports", desc: "Real-time dashboards for collection rates, defaulter lists, and revenue forecasts. Export to Tally-compatible formats." },
    ],
    keywords: "college fee collection software, online fee payment system, student fee management, fee receipt generation, education fee tracking",
  },
  attendance: {
    slug: "attendance",
    icon: Clock,
    label: "Attendance",
    headline: "Attendance,\ntracked.",
    tagline: "03 — Academic",
    description: "Mark, monitor, and report attendance across classes, labs, and events. Biometric integration, parent alerts for low attendance, and NAAC-ready reports.",
    screenshot: "/screenshots/attendance.png",
    color: "#F59E0B",
    stats: [
      { value: "252", label: "Records today" },
      { value: "81.3%", label: "Avg attendance" },
      { value: "Real-time", label: "Parent alerts" },
    ],
    capabilities: [
      { title: "One-tap Marking", desc: "Faculty mark attendance in seconds with class-wise student lists. Support for present, absent, late, and excused statuses." },
      { title: "Biometric Integration", desc: "Connect fingerprint and RFID devices for automated attendance capture. Geo-fenced mobile check-in for field activities." },
      { title: "Shortage Alerts", desc: "Auto-alert students and parents when attendance drops below threshold. Configurable per-subject and per-semester limits." },
      { title: "Analytics Dashboard", desc: "Subject-wise, student-wise, and department-wise attendance analytics. Exportable reports for NAAC and university submissions." },
    ],
    keywords: "student attendance management system, biometric attendance software, college attendance tracking, attendance report generation",
  },
  lms: {
    slug: "lms",
    icon: BookOpen,
    label: "LMS",
    headline: "Learning,\ndelivered.",
    tagline: "04 — Academic",
    description: "A complete learning management system with course content, assignments, quizzes, and grade tracking. Everything faculty and students need in one place.",
    screenshot: "/screenshots/lms.png",
    color: "#8B5CF6",
    stats: [
      { value: "89", label: "Active courses" },
      { value: "6", label: "Semesters" },
      { value: "85%", label: "Avg attendance" },
    ],
    capabilities: [
      { title: "Course Management", desc: "Create courses with modules, lessons, and resources. Assign faculty, set credits, and manage semester mappings." },
      { title: "Assignment Engine", desc: "Create, distribute, and grade assignments digitally. Support for file uploads, rubric-based grading, and plagiarism detection." },
      { title: "Live Quizzes", desc: "Timed MCQ quizzes with auto-grading, question banks, and randomization. Instant result publishing." },
      { title: "Progress Tracking", desc: "Student-wise progress dashboards. Track completion rates, grades, and engagement metrics per course." },
    ],
    keywords: "college LMS, learning management system for institutes, online course management, digital classroom, assignment management",
  },
  exams: {
    slug: "exams",
    icon: ClipboardList,
    label: "Examinations",
    headline: "Exams,\norchestrated.",
    tagline: "05 — Academic",
    description: "Schedule, conduct, and manage examinations with hall allocation, seating arrangements, invigilator assignments, and result processing — all from one module.",
    screenshot: "/screenshots/exams.png",
    color: "#EC4899",
    stats: [
      { value: "5", label: "Exams scheduled" },
      { value: "247", label: "Students enrolled" },
      { value: "Auto", label: "Hall allocation" },
    ],
    capabilities: [
      { title: "Exam Scheduling", desc: "Schedule mid-sems, end-sems, labs, and practicals. Conflict detection ensures no student has overlapping exams." },
      { title: "Seating Arrangement", desc: "Auto-generate seating plans with configurable rules — alternating branches, gap seating, and randomization." },
      { title: "Question Paper Management", desc: "Secure upload, versioning, and distribution of question papers. Role-based access with audit trails." },
      { title: "Result Processing", desc: "Bulk mark entry, moderation, grace marks, and CGPA/SGPA calculation. Publish results with one click." },
    ],
    keywords: "examination management software, exam scheduling system, college exam management, seating arrangement software, result processing",
  },
  hr: {
    slug: "hr",
    icon: Users,
    label: "HR Management",
    headline: "People,\nempowered.",
    tagline: "06 — Operations",
    description: "Complete HR management for educational institutions — employee records, leave management, performance reviews, and compliance tracking.",
    screenshot: "/screenshots/hr.png",
    color: "#14B8A6",
    stats: [
      { value: "164", label: "Total staff" },
      { value: "10", label: "Departments" },
      { value: "3", label: "Pending leaves" },
    ],
    capabilities: [
      { title: "Employee Database", desc: "Centralized records with personal details, qualifications, publications, and service history. Self-service portal for staff." },
      { title: "Leave Management", desc: "Apply, approve, and track leaves with configurable policies. Auto-calculate balances, carry-forwards, and encashments." },
      { title: "Performance Reviews", desc: "360-degree appraisals with self-assessment, peer review, and management evaluation. Track KRAs and development goals." },
      { title: "Compliance", desc: "AICTE, UGC, and university norms compliance tracking. Auto-generate faculty workload reports and qualification matrices." },
    ],
    keywords: "HR management for colleges, faculty management software, employee leave management, institute staff management",
  },
  payroll: {
    slug: "payroll",
    icon: DollarSign,
    label: "Payroll",
    headline: "Payroll,\nprecise.",
    tagline: "07 — Finance & HR",
    description: "Automated salary processing with tax calculations, statutory deductions, and payslip generation. Integrated with HR for attendance-based pay adjustments.",
    screenshot: "/screenshots/payroll.png",
    color: "#F97316",
    stats: [
      { value: "₹6.0L", label: "Monthly disbursement" },
      { value: "Auto", label: "Tax calculation" },
      { value: "1-click", label: "Payslip generation" },
    ],
    capabilities: [
      { title: "Salary Processing", desc: "Configure salary structures with basic, HRA, DA, allowances, and deductions. Process payroll for all employees in one click." },
      { title: "Tax Compliance", desc: "Auto-calculate TDS, PF, ESI, and professional tax. Generate Form 16, 24Q, and annual returns." },
      { title: "Payslip Generation", desc: "Generate and distribute PDF payslips via email and WhatsApp. Employee self-service for payslip history and tax declarations." },
      { title: "Attendance Integration", desc: "Auto-adjust pay based on attendance, leaves, and overtime. Support for loss-of-pay and comp-off calculations." },
    ],
    keywords: "college payroll software, salary processing system, payslip generation, faculty payroll management, education payroll",
  },
  placement: {
    slug: "placement",
    icon: Briefcase,
    label: "Placement",
    headline: "Careers,\nlaunched.",
    tagline: "08 — Placement Cell",
    description: "Manage the entire placement lifecycle — company registrations, job postings, student applications, interview scheduling, and offer tracking.",
    screenshot: "/screenshots/placement.png",
    color: "#6366F1",
    stats: [
      { value: "1,311", label: "Total applicants" },
      { value: "211", label: "Open positions" },
      { value: "₹32L", label: "Highest package" },
    ],
    capabilities: [
      { title: "Company Portal", desc: "Dedicated portal for companies to register, post JDs, and shortlist candidates. Track recruiter interactions and feedback." },
      { title: "Job Board", desc: "Centralized job listings with filters by branch, package, role, and deadline. Students apply with one click." },
      { title: "Interview Management", desc: "Schedule interviews, send calendar invites, and collect recruiter feedback. Support for multi-round selection processes." },
      { title: "Placement Analytics", desc: "Track placement rates, average packages, and company-wise offers. Generate NAAC and NIRF placement reports automatically." },
    ],
    keywords: "college placement management, campus recruitment software, placement cell management, job placement tracking, campus hiring",
  },
  whatsapp: {
    slug: "whatsapp",
    icon: MessageSquare,
    label: "WhatsApp",
    headline: "Messages,\ninstant.",
    tagline: "09 — Communications",
    description: "Send fee reminders, attendance alerts, exam notices, and results directly to students and parents via WhatsApp. Personal and Business API support.",
    screenshot: "/screenshots/whatsapp.png",
    color: "#25D366",
    stats: [
      { value: "Instant", label: "Message delivery" },
      { value: "2", label: "API modes" },
      { value: "Bulk", label: "Send support" },
    ],
    capabilities: [
      { title: "Personal WhatsApp", desc: "Scan QR code to connect your WhatsApp number. Send messages instantly without Meta Business verification." },
      { title: "Business API", desc: "Connect the official Meta Cloud API for verified business messaging. Template-based messaging with delivery tracking." },
      { title: "Message Templates", desc: "Pre-built templates for fee reminders, attendance alerts, exam notices, result notifications, and payment receipts." },
      { title: "Bulk Messaging", desc: "Send messages to hundreds of recipients with rate limiting and delivery status tracking. CSV import for number lists." },
    ],
    keywords: "WhatsApp integration for colleges, bulk WhatsApp messaging, education WhatsApp API, student notification system",
  },
};

export const featureList = Object.values(features);
export default features;
